import { styled } from "goober";
import { useMemo, useState } from "react";
import {
  getAIProvider,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { makeUrl, routes } from "../../lib/urls";
import { GhostPill } from "../GhostPill";
import { ListTable, type SortDirection } from "../ListTable";
import { ProviderLogo } from "../ProviderLogo";
import { Text } from "../Text";
import {
  type AiUsageRecord,
  computeModelUsageRows,
  formatCacheHitRate,
  formatEstimatedCostUsd,
  formatTokenCount,
  type ModelUsageRow,
} from "./aiUsage";
import { NameBody, NameCell, ScrollArea, TableCard } from "./monitoringList";
import { Panel, PanelHeader } from "./workflowsLayout";

type SortColumn = "tokens" | "cache" | "cost";

const GhostModelLogo = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--alpha8);
  flex-shrink: 0;
`;

const GHOST_MODEL_ROWS = [
  { model: 112, provider: 64, tokens: 40, cache: 32, cost: 44 },
  { model: 88, provider: 52, tokens: 36, cache: 28, cost: 40 },
  { model: 136, provider: 72, tokens: 44, cache: 36, cost: 48 },
  { model: 104, provider: 60, tokens: 38, cache: 30, cost: 42 },
  { model: 124, provider: 68, tokens: 42, cache: 34, cost: 46 },
];

function cacheRate(row: ModelUsageRow): number {
  const denominator = row.inputTokens + row.cacheReadTokens;
  return denominator === 0 ? 0 : row.cacheReadTokens / denominator;
}

type Props = {
  records: readonly AiUsageRecord[];
  prices: ReadonlyMap<string, number>;
};

export function AiModelsTablePanel({ records, prices }: Props) {
  const [sortColumn, setSortColumn] = useState<SortColumn>("cost");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const rows = useMemo(
    () => computeModelUsageRows(records, prices),
    [records, prices]
  );

  const sorted = useMemo(() => {
    const dir = sortDirection === "asc" ? 1 : -1;
    const value = (row: ModelUsageRow): number => {
      switch (sortColumn) {
        case "tokens":
          return row.totalTokens;
        case "cache":
          return cacheRate(row);
        case "cost":
          return row.estimatedCostUsd ?? 0;
      }
    };
    return [...rows].sort(
      (a, b) => dir * (value(a) - value(b)) || a.model.localeCompare(b.model)
    );
  }, [rows, sortColumn, sortDirection]);

  const onSort = (column: string) => {
    const next = column as SortColumn;
    if (next === sortColumn) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(next);
      setSortDirection("desc");
    }
  };

  const items =
    rows.length === 0
      ? GHOST_MODEL_ROWS.map((row, index) => ({
          key: `ghost-${index}`,
          model: (
            <NameCell aria-hidden>
              <GhostModelLogo />
              <NameBody>
                <GhostPill style={{ width: row.model }} />
                <GhostPill style={{ width: row.provider }} />
              </NameBody>
            </NameCell>
          ),
          tokens: <GhostPill aria-hidden style={{ width: row.tokens }} />,
          cache: <GhostPill aria-hidden style={{ width: row.cache }} />,
          cost: <GhostPill aria-hidden style={{ width: row.cost }} />,
        }))
      : sorted.map((row) => {
          const providerId = resolveAIProviderId({
            provider: row.provider,
          });
          // A model whose usage came through a connector with no configured
          // ai_provider (aiProviderId falls back to the connector id) has no
          // provider modal to open, so link to the connector instead.
          const connectorBacked =
            row.aiProviderId != null && row.aiProviderId === row.connectorId;
          return {
            key: row.model,
            link: connectorBacked
              ? makeUrl(routes.modal.connector, {
                  id: row.connectorId!,
                  tab: "usage",
                })
              : makeUrl(routes.modal.aiProvider, {
                  id: row.aiProviderId ?? MANAGED_AI_PROVIDER_ID,
                }),
            model: (
              <NameCell>
                <ConnectorLogoContainer
                  size={32}
                  borderRadius={8}
                  outline={false}
                  hoverBackground={false}
                >
                  <ProviderLogo provider={providerId} size={16} />
                </ConnectorLogoContainer>
                <NameBody>
                  <Text variant="text-regular-md" ellipsis>
                    {row.model}
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {getAIProvider(providerId).label}
                  </Text>
                </NameBody>
              </NameCell>
            ),
            tokens: (
              <Text variant="text-small">
                {formatTokenCount(row.totalTokens)}
              </Text>
            ),
            cache: (
              <Text variant="text-small">
                {formatCacheHitRate(row.cacheReadTokens, row.inputTokens)}
              </Text>
            ),
            cost: (
              <Text variant="text-small">
                {formatEstimatedCostUsd(row.estimatedCostUsd)}
              </Text>
            ),
          };
        });

  return (
    <Panel style={{ minHeight: 0 }}>
      <PanelHeader>
        <Text variant="text-small-md">Models</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          Tokens, cache hit rate, and estimated cost per model in the last 30
          days.
        </Text>
      </PanelHeader>
      <TableCard aria-hidden={rows.length === 0 || undefined}>
        <ScrollArea>
          <ListTable
            headers={{
              model: "Model",
              tokens: "Tokens",
              cache: "Cache",
              cost: "Est. cost",
            }}
            columns={{
              model: "flex",
              tokens: "md",
              cache: "md",
              cost: "md",
            }}
            sortableColumns={
              rows.length === 0 ? [] : ["tokens", "cache", "cost"]
            }
            sortColumn={rows.length === 0 ? null : sortColumn}
            sortDirection={sortDirection}
            onSort={rows.length === 0 ? undefined : onSort}
            items={items}
          />
        </ScrollArea>
      </TableCard>
    </Panel>
  );
}
